import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ArrowLeft, Send, MapPin, Trophy, UserPlus, UserCheck, Wifi, WifiOff } from 'lucide-react';
import { TierPill, getUserTier } from '../components/TierBadge';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Messages = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [match, setMatch] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // WebSocket connection
  const { isConnected, addMessageHandler, sendTyping, markAsRead } = useWebSocket(token);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getPhotoUrl = (photoPath) => {
    if (!photoPath) return null;
    if (photoPath.startsWith('http')) return photoPath;
    // Handle both /uploads/ (old) and /api/photos/ (new) paths
    if (photoPath.startsWith('/uploads/')) {
      const filename = photoPath.replace('/uploads/', '');
      return `${BACKEND_URL}/api/photos/${filename}`;
    }
    return `${BACKEND_URL}${photoPath}`;
  };

  // Handle incoming WebSocket messages
  useEffect(() => {
    const removeHandler = addMessageHandler((data) => {
      if (data.type === 'new_message' && data.match_id === matchId) {
        setMessages(prev => [...prev, data.message]);
        // Mark as read since we're viewing this conversation
        markAsRead(matchId);
      } else if (data.type === 'typing' && data.match_id === matchId) {
        setOtherUserTyping(data.is_typing);
        // Clear typing indicator after 3 seconds
        if (data.is_typing) {
          setTimeout(() => setOtherUserTyping(false), 3000);
        }
      }
    });

    return removeHandler;
  }, [matchId, addMessageHandler, markAsRead]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [matchRes, messagesRes] = await Promise.all([
          axios.get(`${API}/matches/${matchId}`),
          axios.get(`${API}/messages/${matchId}`)
        ]);
        setMatch(matchRes.data);
        setMessages(messagesRes.data);
        // Mark messages as read
        markAsRead(matchId);
        // Fetch follow status for the other user
        const otherUserId = matchRes.data?.other_user?.id;
        if (otherUserId) {
          try {
            const followRes = await axios.get(`${API}/social/follow-status/${otherUserId}`);
            setIsFollowing(followRes.data.following);
          } catch { /* silent */ }
        }
      } catch (error) {
        toast.error('Failed to load conversation');
        navigate('/messages');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [matchId, navigate, markAsRead]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, otherUserTyping]);

  // Handle typing indicator
  const handleTyping = useCallback(() => {
    if (!isTyping) {
      setIsTyping(true);
      sendTyping(matchId, true);
    }

    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout to stop typing indicator
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      sendTyping(matchId, false);
    }, 2000);
  }, [isTyping, matchId, sendTyping]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    // Stop typing indicator
    setIsTyping(false);
    sendTyping(matchId, false);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/messages/${matchId}`, {
        content: newMessage.trim()
      });
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getDefaultPhoto = (name) => {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'Golfer')}&size=200&background=076652&color=fff&font-size=0.4`;
  };

  const handleFollowToggle = async () => {
    if (!otherUser?.id) return;
    try {
      const res = await axios.post(`${API}/social/follow/${otherUser.id}`);
      setIsFollowing(res.data.following);
    } catch {
      toast.error('Failed to update follow');
    }
  };

  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading conversation...</div>
      </div>
    );
  }

  const otherUser = match?.other_user;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-xl border-b border-border px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/social')}
            className="rounded-full"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>

          <Avatar className="w-10 h-10 border-2 border-primary/20">
            <AvatarImage
              src={getPhotoUrl(otherUser?.profile_photo) || getDefaultPhoto(otherUser?.display_name)}
              alt={otherUser?.display_name}
            />
            <AvatarFallback className="bg-primary text-primary-foreground">
              {otherUser?.display_name?.charAt(0) || 'G'}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <h1 className="font-semibold text-foreground truncate">
                {otherUser?.display_name}
              </h1>
              {getUserTier(otherUser) && <TierPill tier={getUserTier(otherUser)} size="xs" />}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {otherUserTyping ? (
                <span className="text-primary animate-pulse">typing...</span>
              ) : (
                <>
                  {otherUser?.location && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {otherUser.location}
                    </span>
                  )}
                  {otherUser?.handicap && (
                    <span className="flex items-center gap-1">
                      <Trophy className="w-3 h-3" />
                      {otherUser.handicap}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Connection status indicator */}
          <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} title={isConnected ? 'Connected' : 'Reconnecting...'} />

          <Button
            variant={isFollowing ? "secondary" : "outline"}
            size="sm"
            onClick={handleFollowToggle}
            className="rounded-full gap-1"
            data-testid="follow-user-btn"
          >
            {isFollowing ? (
              <><UserCheck className="w-4 h-4" /> Following</>
            ) : (
              <><UserPlus className="w-4 h-4" /> Follow</>
            )}
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="max-w-lg mx-auto space-y-4">
          {messages.length === 0 && (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                Start the conversation! Say hello to {otherUser?.display_name}.
              </p>
            </div>
          )}

          {messages.map((message) => {
            const isOwn = message.sender_id === user?.id;
            return (
              <div
                key={message.id}
                className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                data-testid={`message-${message.id}`}
              >
                <div
                  className={`max-w-[75%] px-4 py-3 ${
                    isOwn
                      ? 'chat-bubble-sent'
                      : 'chat-bubble-received'
                  }`}
                >
                  <p className="text-sm">{message.content}</p>
                  <p className={`text-xs mt-1 ${isOwn ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                    {formatTime(message.created_at)}
                  </p>
                </div>
              </div>
            );
          })}

          {/* Typing indicator */}
          {otherUserTyping && (
            <div className="flex justify-start">
              <div className="chat-bubble-received px-4 py-3">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      <div className="sticky bottom-0 bg-background/80 backdrop-blur-xl border-t border-border px-4 py-4">
        <form onSubmit={handleSend} className="max-w-lg mx-auto flex items-center gap-3">
          <Input
            value={newMessage}
            onChange={(e) => {
              setNewMessage(e.target.value);
              handleTyping();
            }}
            placeholder="Type a message..."
            className="flex-1 h-12 rounded-full bg-muted border-0 px-6"
            data-testid="message-input"
          />
          <Button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="w-12 h-12 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground btn-press"
            data-testid="send-btn"
          >
            <Send className="w-5 h-5" />
          </Button>
        </form>
      </div>
    </div>
  );
};

export default Messages;
